/**
 * One parser + one presenter for every backend error, on every transport.
 *
 * The backend now answers all errors with a single envelope:
 *   {"detail": {"code", "i18n_key", "params", "message", "request_id"}}
 * but this module still tolerates every legacy shape it might meet:
 * camelized axios bodies (detail.i18nKey / requestId — humps camelizes error
 * bodies too), raw-fetch snake_case bodies (chat SSE, console client),
 * plain-string details, SSE error frames ({type:'error', code, message}),
 * and network failures with no response at all.
 *
 * Display text comes from the frontend map (errors.json, en/ru/uz), keyed by
 * `code` — the backend `message` is an English developer fallback only.
 */

import { toast } from 'sonner';
import i18n from '../../i18n';

/** Normalize any error-ish input into {code, message, params, status, requestId}. */
export function parseApiError(input) {
  if (!input) return { code: 'network_error', message: null, params: {}, status: null, requestId: null };

  // Error WITHOUT a response: either a fetch/SSE helper that attached our own
  // snake_case code, or a transport failure. Axios errors are Errors too but
  // carry ERR_* codes ('ERR_BAD_REQUEST') — never treat those as app codes;
  // an axios error WITH a response falls through to the envelope path below.
  if (input instanceof Error && !input.response) {
    const ownCode = typeof input.code === 'string' && /^[a-z][a-z0-9_]*$/.test(input.code)
      ? input.code
      : null;
    if (!ownCode && (input.isAxiosError || input.name === 'TypeError')) {
      return { code: 'network_error', message: null, params: {}, status: null, requestId: null };
    }
    return {
      code: ownCode,
      message: input.message || null,
      params: input.params || {},
      status: input.status ?? null,
      requestId: input.requestId ?? null,
    };
  }

  // Axios error (has .response) → unwrap; axios/plain response → use as-is.
  const response = input.response || input;
  const status = response.status ?? null;
  const data = response.data !== undefined ? response.data : response;

  if (!data || typeof data !== 'object') {
    return { code: status ? null : 'network_error', message: typeof data === 'string' ? data : null, params: {}, status, requestId: null };
  }

  // SSE error frame or already-parsed envelope payload: {code, message, ...}
  const detail = data.detail !== undefined ? data.detail : data;

  if (typeof detail === 'string') {
    // Legacy string detail: machine codes are single snake_case tokens —
    // treat those as a code, sentences as a message.
    const isCode = /^[a-z0-9_]+$/i.test(detail) && !detail.includes(' ');
    return { code: isCode ? detail : null, message: isCode ? null : detail, params: {}, status, requestId: null };
  }

  if (detail && typeof detail === 'object') {
    return {
      code: detail.code || null,
      message: typeof detail.message === 'string' ? detail.message : null,
      params: detail.params || {},
      status,
      requestId: detail.request_id || detail.requestId || null,
    };
  }

  return { code: null, message: typeof data.message === 'string' ? data.message : null, params: {}, status, requestId: null };
}

/** i18next interpolation renders arrays poorly — pre-join them for display. */
function displayParams(params) {
  const out = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    out[key] = Array.isArray(value) ? value.join(', ') : value;
  });
  return out;
}

/**
 * Resolve display text for an API error.
 * Chain: per-call override → errors.json by code → generic *_not_found →
 * backend message → unknown_error.
 *
 * `overrides` maps code → string | (parsed) => string.
 */
export function apiErrorMessage(input, overrides = null) {
  const parsed = input && input.code !== undefined && input.params !== undefined && !(input instanceof Error)
    ? input
    : parseApiError(input);
  const { code, message, params } = parsed;

  if (code && overrides && overrides[code] !== undefined) {
    const override = overrides[code];
    return typeof override === 'function' ? override(parsed) : override;
  }

  // validation_error is inherently dynamic — the backend message is already
  // a "field: problem" summary of the first failure.
  if (code === 'validation_error' && message) return message;

  if (code) {
    const key = `errors:${code}`;
    if (i18n.exists(key)) return i18n.t(key, displayParams(params));
    if (code.endsWith('_not_found') && i18n.exists('errors:resource_not_found')) {
      return i18n.t('errors:resource_not_found');
    }
  }

  if (message) return message;
  return i18n.t('errors:unknown_error');
}

/** The one way to toast an API error. */
export function toastApiError(input, overrides = null) {
  toast.error(apiErrorMessage(input, overrides));
}
